/**
 * Help content admin: the tours of a page. Each step points at an anchor from the help registry
 * (or is a centred card), with a title, body, placement and an optional route ("?tab=plan" opens a
 * tab first). Steps can be reordered. "Start on first visit" auto-starts the tour once per person
 * (never again once they finished or skipped it, and not while they switched off the app's tours);
 * "Mark it as updated" raises the version so people who saw it see it marked Updated in the ? menu.
 * Saves with PUT api/help/admin/tours/{tourKey}; the steps sent replace the tour's steps.
 */

import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  IconAlertCircle,
  IconArrowBackUp,
  IconArrowDown,
  IconArrowUp,
  IconDeviceFloppy,
  IconPlayerPlay,
  IconPlus,
  IconRoute,
  IconTrash,
} from '@tabler/icons-react';
import { Alert, Badge, Button, Card, Checkbox, Group, Select, SimpleGrid, Stack, Switch, Text, TextInput, Textarea, Title, Tooltip } from '@/components/ui';
import ActionIcon from '@/components/ui/ActionIcon';
import { EmptyState } from '@/components/ui/data-display';
import { ConfirmDialog } from '@/components/advanced';
import { toast } from '@/components/ui/providers/AlertProvider';
import { useHelpService, type HelpPageView, type HelpTourView } from '@actschurch/ccms-sdk';
import { helpKeys, useHelpContext, type HelpPageDef } from '@/components/help';
import { HelpHtmlField } from './HelpHtmlField';
import {
  PLACEMENT_OPTIONS,
  TOUR_KEY_PATTERN,
  errorText,
  newStep,
  newTourForm,
  stepsToView,
  tourFormSignature,
  tourFormToSaveRequest,
  tourToForm,
  type StepForm,
  type TourForm,
} from './helpAdminUtils';

const CENTRED = '__centred__';

interface HelpToursEditorProps {
  pageKey: string;
  page: HelpPageView | null;
  def: HelpPageDef | undefined;
  onTourSaved: (tour: HelpTourView) => void;
  onTourDeleted: (tourKey: string) => void;
}

export function HelpToursEditor({ pageKey, page, def, onTourSaved, onTourDeleted }: HelpToursEditorProps) {
  const tours = useMemo(() => page?.tours ?? [], [page?.tours]);
  const [selectedKey, setSelectedKey] = useState<string | null>(tours[0]?.tourKey ?? null);
  const [draftNew, setDraftNew] = useState<TourForm | null>(null);

  useEffect(() => {
    if (selectedKey && !tours.some((t) => t.tourKey === selectedKey) && !draftNew) setSelectedKey(tours[0]?.tourKey ?? null);
    if (!selectedKey && !draftNew && tours.length > 0) setSelectedKey(tours[0].tourKey);
  }, [tours, selectedKey, draftNew]);

  if (!page) {
    return (
      <Alert color="blue" icon={<IconAlertCircle size={16} />} title="Save the page first">
        Tours belong to a page. Save the page help on the Page tab, then add tours here.
      </Alert>
    );
  }

  const selectedTour = tours.find((t) => t.tourKey === selectedKey) ?? null;

  return (
    <Stack gap="lg">
      <Card withBorder radius="md" padding="md">
        <Group justify="space-between" wrap="wrap" gap="sm">
          <Group gap="xs" wrap="wrap">
            {tours.map((t) => (
              <Button
                key={t.tourKey}
                size="compact-sm"
                variant={!draftNew && selectedKey === t.tourKey ? 'filled' : 'light'}
                color={t.isActive ? undefined : 'gray'}
                leftSection={<IconRoute size={14} />}
                onClick={() => {
                  setDraftNew(null);
                  setSelectedKey(t.tourKey);
                }}
              >
                {t.title || t.tourKey}
                {!t.isActive ? ' (inactive)' : ''}
              </Button>
            ))}
            {draftNew && (
              <Button size="compact-sm" variant="filled" leftSection={<IconPlus size={14} />}>
                New tour
              </Button>
            )}
            {tours.length === 0 && !draftNew && (
              <Text size="sm" c="dimmed">
                No tours yet.
              </Text>
            )}
          </Group>
          <Button
            size="compact-sm"
            leftSection={<IconPlus size={14} />}
            onClick={() => setDraftNew(newTourForm(pageKey, tours.map((t) => t.tourKey)))}
            disabled={!!draftNew}
          >
            New tour
          </Button>
        </Group>
      </Card>

      {draftNew ? (
        <TourFormEditor
          key={`new-${draftNew.tourKey}`}
          pageKey={pageKey}
          page={page}
          def={def}
          initial={draftNew}
          existingKeys={tours.map((t) => t.tourKey)}
          onSaved={(tour) => {
            setDraftNew(null);
            setSelectedKey(tour.tourKey);
            onTourSaved(tour);
          }}
          onCancelNew={() => setDraftNew(null)}
          onDeleted={() => undefined}
        />
      ) : selectedTour ? (
        <TourFormEditor
          key={`${selectedTour.tourKey}-${selectedTour.version}`}
          pageKey={pageKey}
          page={page}
          def={def}
          initial={tourToForm(selectedTour)}
          existingKeys={tours.map((t) => t.tourKey)}
          onSaved={onTourSaved}
          onDeleted={() => {
            onTourDeleted(selectedTour.tourKey);
            setSelectedKey(null);
          }}
        />
      ) : (
        <Card withBorder radius="md" padding="lg">
          <EmptyState
            icon={<IconRoute size={40} />}
            title="No tour yet"
            description="A tour walks people through the page one element at a time."
            actionLabel="New tour"
            onAction={() => setDraftNew(newTourForm(pageKey, tours.map((t) => t.tourKey)))}
            size="sm"
          />
        </Card>
      )}
    </Stack>
  );
}

// ---------------------------------------------------------------- one tour

interface TourFormEditorProps {
  pageKey: string;
  page: HelpPageView;
  def: HelpPageDef | undefined;
  initial: TourForm;
  existingKeys: string[];
  onSaved: (tour: HelpTourView) => void;
  onDeleted: () => void;
  onCancelNew?: () => void;
}

function TourFormEditor({ pageKey, page, def, initial, existingKeys, onSaved, onDeleted, onCancelNew }: TourFormEditorProps) {
  const help = useHelpService();
  const ctx = useHelpContext();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [form, setForm] = useState<TourForm>(initial);
  const [baseline, setBaseline] = useState<TourForm>(initial);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const defaultPreview = page.route || def?.route || '';
  const [previewRoute, setPreviewRoute] = useState(defaultPreview);

  const dirty = form.isNew || tourFormSignature(pageKey, form) !== tourFormSignature(pageKey, baseline);

  const anchors = def?.anchors ?? [];
  const anchorById = useMemo(() => new Map(anchors.map((a) => [a.id, a] as const)), [anchors]);
  const targetData = useMemo(() => {
    const unknown = Array.from(new Set(form.steps.map((s) => s.target.trim()).filter((t) => t && !anchorById.has(t))));
    const groups: { group: string; items: { value: string; label: string }[] }[] = [
      { group: 'No element', items: [{ value: CENTRED, label: 'Centred card' }] },
    ];
    if (anchors.length > 0) {
      groups.push({ group: 'Elements on this page', items: anchors.map((a) => ({ value: a.id, label: a.tab ? `${a.what} (${a.tab} tab)` : a.what })) });
    }
    if (unknown.length > 0) groups.push({ group: 'Not on the page any more', items: unknown.map((id) => ({ value: id, label: id })) });
    return groups;
  }, [anchors, anchorById, form.steps]);

  const set = (patch: Partial<TourForm>) => setForm((f) => ({ ...f, ...patch }));
  const setStep = (uid: string, patch: Partial<StepForm>, absorb = false) => {
    const apply = (f: TourForm) => ({ ...f, steps: f.steps.map((s) => (s.uid === uid ? { ...s, ...patch } : s)) });
    setForm(apply);
    if (absorb) setBaseline(apply);
  };
  const move = (index: number, delta: number) =>
    setForm((f) => {
      const steps = [...f.steps];
      const to = index + delta;
      if (to < 0 || to >= steps.length) return f;
      [steps[index], steps[to]] = [steps[to], steps[index]];
      return { ...f, steps };
    });
  const removeStep = (uid: string) => setForm((f) => ({ ...f, steps: f.steps.filter((s) => s.uid !== uid) }));

  const keyError = !form.isNew
    ? null
    : !TOUR_KEY_PATTERN.test(form.tourKey)
      ? 'Use lower case letters, numbers, dots and dashes (3 to 120 characters).'
      : existingKeys.includes(form.tourKey)
        ? 'This page already has a tour with this key.'
        : null;
  const titleError = form.title.trim() ? null : 'Give the tour a title.';
  const stepsError = form.steps.length === 0 ? 'Add at least one step.' : form.steps.some((s) => !s.title.trim()) ? 'Every step needs a title.' : null;
  const canSave = dirty && !keyError && !titleError && !stepsError;

  const save = useMutation({
    mutationFn: () => help.saveHelpTour(form.tourKey.trim(), tourFormToSaveRequest(pageKey, form)),
    onSuccess: (tour) => {
      setSaveError(null);
      const next = tourToForm(tour);
      setForm(next);
      setBaseline(next);
      queryClient.invalidateQueries({ queryKey: helpKeys.page(pageKey) });
      toast.success('Tour saved', form.bumpVersion ? 'People who saw it before see it marked Updated in the ? menu.' : tour.title);
      onSaved(tour);
    },
    onError: (err) => {
      const msg = errorText(err, 'The tour could not be saved. Try again.');
      setSaveError(msg);
      toast.error('Could not save the tour', msg);
    },
  });

  const remove = useMutation({
    mutationFn: () => help.deleteHelpTour(form.tourKey),
    onSuccess: () => {
      setConfirmDelete(false);
      queryClient.invalidateQueries({ queryKey: helpKeys.page(pageKey) });
      toast.success('Tour removed', form.title);
      onDeleted();
    },
    onError: (err) => toast.error('Could not remove the tour', errorText(err, 'Try again.')),
  });

  const previewTemplate = previewRoute.includes(':');
  const runPreview = () => {
    if (!ctx) return;
    const route = previewRoute.trim();
    if (route && !previewTemplate) navigate(route);
    void ctx.startTour(
      {
        tourKey: form.tourKey,
        pageKey,
        title: form.title || 'Tour',
        description: form.description || null,
        version: form.version,
        autoStart: false,
        isActive: true,
        steps: stepsToView(form.steps),
      },
      { preview: true, firstWaitMs: 8000, startDelayMs: 300 }
    );
  };

  return (
    <Stack gap="lg">
      <Card withBorder radius="md" padding="lg">
        <Stack gap="md">
          <Group justify="space-between" wrap="wrap" gap="sm">
            <Title order={4}>{form.isNew ? 'New tour' : 'Tour'}</Title>
            {!form.isNew && (
              <Badge variant="light" styles={{ root: { textTransform: 'none' } }}>
                Version {form.version}
              </Badge>
            )}
          </Group>
          <SimpleGrid cols={{ base: 1, sm: 2 }}>
            <TextInput
              label="Tour key"
              description={form.isNew ? 'Lower case, for example events.sources.intro.' : 'Set when the tour was created.'}
              value={form.tourKey}
              onChange={(e) => set({ tourKey: e.currentTarget.value.toLowerCase() })}
              disabled={!form.isNew}
              error={keyError}
              maxLength={120}
            />
            <TextInput label="Title" value={form.title} onChange={(e) => set({ title: e.currentTarget.value })} error={titleError} required maxLength={200} />
          </SimpleGrid>
          <Textarea label="Description" description="One line shown in the help panel." value={form.description} onChange={(e) => set({ description: e.currentTarget.value })} autosize minRows={1} maxRows={3} maxLength={500} />
          <Group gap="xl" wrap="wrap">
            <Switch label="Active" checked={form.isActive} onChange={(e) => set({ isActive: e.currentTarget.checked })} />
            <Switch
              label="Start on first visit"
              description="Starts by itself the first time someone opens the page, unless they switched off tours for this app."
              checked={form.autoStart}
              onChange={(e) => set({ autoStart: e.currentTarget.checked })}
            />
          </Group>
          {!form.isNew && (
            <Checkbox
              label="Mark it as updated"
              description={`Raises the version to ${form.version + 1} on save. People who finished or skipped it see it marked Updated in the ? menu and can take it again. It does not open by itself again.`}
              checked={form.bumpVersion}
              onChange={(e) => set({ bumpVersion: e.currentTarget.checked })}
            />
          )}
        </Stack>
      </Card>

      <Stack gap="sm">
        <Group justify="space-between" wrap="wrap">
          <Title order={4}>Steps</Title>
          <Text size="xs" c="dimmed">
            {anchors.length > 0 ? `${anchors.length} elements on this page can be pointed at.` : 'This page has no marked elements yet. Use centred cards.'}
          </Text>
        </Group>
        {form.steps.map((s, i) => {
          const anchor = anchorById.get(s.target.trim());
          const centred = !s.target.trim();
          return (
            <Card key={s.uid} withBorder radius="md" padding="md">
              <Stack gap="sm">
                <Group justify="space-between" wrap="nowrap">
                  <Text fw={700} size="sm">
                    Step {i + 1}
                  </Text>
                  <Group gap={4} wrap="nowrap">
                    <Tooltip label="Move up" withArrow>
                      <ActionIcon variant="subtle" aria-label={`Move step ${i + 1} up`} disabled={i === 0} onClick={() => move(i, -1)}>
                        <IconArrowUp size={16} />
                      </ActionIcon>
                    </Tooltip>
                    <Tooltip label="Move down" withArrow>
                      <ActionIcon variant="subtle" aria-label={`Move step ${i + 1} down`} disabled={i === form.steps.length - 1} onClick={() => move(i, 1)}>
                        <IconArrowDown size={16} />
                      </ActionIcon>
                    </Tooltip>
                    <Tooltip label="Remove step" withArrow>
                      <ActionIcon variant="subtle" color="red" aria-label={`Remove step ${i + 1}`} onClick={() => removeStep(s.uid)}>
                        <IconTrash size={16} />
                      </ActionIcon>
                    </Tooltip>
                  </Group>
                </Group>
                <SimpleGrid cols={{ base: 1, sm: 3 }}>
                  <Select
                    label="Point at"
                    data={targetData}
                    value={centred ? CENTRED : s.target}
                    onChange={(v) => {
                      const target = !v || v === CENTRED ? '' : v;
                      const a = anchorById.get(target);
                      setStep(s.uid, {
                        target,
                        placement: !target ? 'center' : s.placement === 'center' ? 'bottom' : s.placement,
                        route: a?.tab && !s.route.trim() ? `?tab=${a.tab}` : s.route,
                      });
                    }}
                    searchable
                    allowDeselect={false}
                    description={anchor ? anchor.id : centred ? 'A card in the middle of the screen.' : 'This element is not on the page any more.'}
                    error={!centred && !anchor ? 'Pick an element that is on the page.' : undefined}
                  />
                  <Select
                    label="Card position"
                    data={PLACEMENT_OPTIONS}
                    value={centred ? 'center' : s.placement}
                    onChange={(v) => setStep(s.uid, { placement: v || 'bottom' })}
                    disabled={centred}
                    allowDeselect={false}
                  />
                  <TextInput
                    label="Open first (optional)"
                    placeholder="?tab=plan"
                    description="A tab or page to open before this step."
                    value={s.route}
                    onChange={(e) => setStep(s.uid, { route: e.currentTarget.value })}
                    maxLength={300}
                  />
                </SimpleGrid>
                <TextInput label="Step title" value={s.title} onChange={(e) => setStep(s.uid, { title: e.currentTarget.value })} required maxLength={200} />
                <HelpHtmlField label="Step text" value={s.bodyHtml} onChange={(html, absorb) => setStep(s.uid, { bodyHtml: html }, absorb)} minHeight={90} />
              </Stack>
            </Card>
          );
        })}
        <Group>
          <Button variant="light" leftSection={<IconPlus size={16} />} onClick={() => setForm((f) => ({ ...f, steps: [...f.steps, newStep()] }))}>
            Add step
          </Button>
        </Group>
      </Stack>

      <Card withBorder radius="md" padding="md">
        <Stack gap="xs">
          <Group gap="sm" wrap="wrap" align="flex-end">
            <TextInput
              label="Preview on"
              description={previewTemplate ? 'Replace :id with a real id to preview a detail page.' : 'The tour opens this page in preview. Nothing is recorded.'}
              value={previewRoute}
              onChange={(e) => setPreviewRoute(e.currentTarget.value)}
              style={{ flex: 1, minWidth: 220 }}
            />
            <Tooltip label={dirty ? 'Save first. Previewing leaves this page.' : 'Open the page and run the tour'} withArrow>
              <Button variant="light" leftSection={<IconPlayerPlay size={16} />} onClick={runPreview} disabled={dirty || previewTemplate || form.steps.length === 0 || !ctx}>
                Run this tour
              </Button>
            </Tooltip>
          </Group>
        </Stack>
      </Card>

      {(saveError || stepsError) && (
        <Alert color={saveError ? 'red' : 'orange'} icon={<IconAlertCircle size={16} />} title={saveError ? 'Not saved' : 'Before you save'}>
          {saveError || stepsError}
        </Alert>
      )}

      <Card withBorder radius="md" padding="md" style={{ position: 'sticky', bottom: 12, zIndex: 5 }}>
        <Group justify="space-between" wrap="wrap" gap="sm">
          <Group gap="xs">
            {form.isNew ? (
              <Button variant="subtle" color="gray" onClick={onCancelNew}>
                Cancel
              </Button>
            ) : (
              <Button variant="subtle" color="red" leftSection={<IconTrash size={16} />} onClick={() => setConfirmDelete(true)} disabled={save.isPending}>
                Remove tour
              </Button>
            )}
            <Text size="sm" c={dirty ? 'orange' : 'dimmed'}>
              {form.isNew ? 'Not saved yet' : dirty ? 'Unsaved changes' : 'Saved'}
            </Text>
          </Group>
          <Group gap="xs">
            {!form.isNew && (
              <Button variant="default" leftSection={<IconArrowBackUp size={16} />} onClick={() => setForm(baseline)} disabled={!dirty || save.isPending}>
                Discard
              </Button>
            )}
            <Button leftSection={<IconDeviceFloppy size={16} />} onClick={() => save.mutate()} loading={save.isPending} disabled={!canSave}>
              Save tour
            </Button>
          </Group>
        </Group>
      </Card>

      <ConfirmDialog
        opened={confirmDelete}
        onClose={() => !remove.isPending && setConfirmDelete(false)}
        onConfirm={() => remove.mutate()}
        closeOnConfirm={false}
        loading={remove.isPending}
        type="danger"
        title="Remove this tour?"
        message="People no longer see it on this page."
        confirmLabel="Remove tour"
        cancelLabel="Keep it"
      />
    </Stack>
  );
}
